<script lang="ts" setup>
  import { Button, ButtonVariant } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { computed, nextTick, ref } from 'vue';
  import { useFilters } from '../../../composables';
  import type { Filter } from '../../../store/';
  import { FilterTypes } from '../../../utils';
  import { ActiveFilterChip } from '../ActiveFilterChip';
  import {
    activeFiltersRowClass,
    clearFiltersButtonClass,
    visuallyHiddenClass,
  } from './ActiveFiltersRow.css';

  const props = withDefaults(
    defineProps<{
      filterNamespace: string;
      persistent?: boolean;
      disabled?: boolean;
    }>(),
    {
      persistent: false,
      disabled: false,
    },
  );

  const { t } = useLocale('table');

  const MIN_FILLED_FILTERS_TO_CLEAR = 1;

  const emit = defineEmits<{
    removeFilter: [filter: Filter];
    clearFilters: [];
  }>();

  const currentFilterStore = useFilters(props.filterNamespace);

  const rowRef = ref<HTMLElement | null>(null);
  const liveAnnouncement = ref('');

  const activeFilters = computed(() => currentFilterStore.getFilters.value);

  const chipFilters = computed(
    () =>
      activeFilters.value?.filter(
        (filter) => filter.filterType !== FilterTypes.TEXT_SEARCH,
      ) ?? [],
  );

  const filledFiltersCount = computed(
    () =>
      chipFilters.value.filter((filter) => filter.modelValue !== undefined)
        .length,
  );

  const showClearFilters = computed(
    () => filledFiltersCount.value >= MIN_FILLED_FILTERS_TO_CLEAR,
  );

  /*
        Removing a chip (or its remove button) unmounts the element that holds
        focus, which would silently drop focus to <body> for keyboard users: hand
        it to the closest equivalent control instead.
      */
  const moveFocusAfterMutation = (preferredIndex: number) => {
    void nextTick(() => {
      const removeButtons = rowRef.value?.querySelectorAll<HTMLButtonElement>(
        'button[data-testid$="-remove"]',
      );
      if (removeButtons?.length) {
        removeButtons[
          Math.min(preferredIndex, removeButtons.length - 1)
        ]?.focus();
        return;
      }
      const fallback =
        rowRef.value?.querySelector<HTMLButtonElement>(
          '[data-testid="table-filters-clear"]',
        ) ??
        document.querySelector<HTMLButtonElement>(
          '[data-testid="table-filters-trigger"]',
        ) ??
        rowRef.value?.querySelector<HTMLButtonElement>(
          'button[data-testid$="-main"]',
        );
      fallback?.focus();
    });
  };

  const onRemoveFilter = (filter: Filter) => {
    const removedIndex = chipFilters.value.findIndex(
      (chip) => chip.filterFieldName === filter.filterFieldName,
    );
    if (props.persistent) {
      currentFilterStore.applyFilterValue({
        filterFieldName: filter.filterFieldName,
        filterType: filter.filterType,
        value: undefined,
      });
    } else {
      currentFilterStore.removeFilter(filter.filterFieldName);
    }
    emit('removeFilter', filter);
    liveAnnouncement.value = t('filters.filter_removed', {
      label: filter.filterLabel,
    });
    moveFocusAfterMutation(Math.max(removedIndex, 0));
  };

  const onClearFilters = () => {
    currentFilterStore.clearAllFilters(props.persistent);
    emit('clearFilters');
    liveAnnouncement.value = t('filters.filters_cleared');
    moveFocusAfterMutation(0);
  };
</script>

<template>
  <div
    ref="rowRef"
    :class="activeFiltersRowClass"
    v-if="activeFilters && activeFilters.length > 0"
  >
    <template v-for="filter in activeFilters" :key="filter.filterFieldName">
      <ActiveFilterChip
        v-if="filter.filterType !== FilterTypes.TEXT_SEARCH"
        :filter-namespace="filterNamespace"
        :persistent="persistent"
        :disabled="disabled"
        v-bind="{ filter }"
        @removeFilter="onRemoveFilter"
      >
        <template #additional-filters>
          <slot name="additional-filters"></slot>
        </template>
      </ActiveFilterChip>
    </template>
    <Button
      v-if="showClearFilters"
      :variant="ButtonVariant.Inline"
      :disabled="disabled"
      :class="clearFiltersButtonClass"
      test-id="table-filters-clear"
      @click="onClearFilters"
    >
      {{ t('filters.clear_filters') }}
    </Button>
  </div>
  <span role="status" :class="visuallyHiddenClass">
    {{ liveAnnouncement }}
  </span>
</template>
